import { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { User } from '../interfaces/user.interface';
import UserOnboardingPopup from '../components/UserOnboardingPopup';
import { auth, db } from '../firebase';

interface UserContextType {
  user: User | null;
  loading: boolean;
  refreshUser: () => Promise<void>;
}

const UserContext = createContext<UserContextType>({
  user: null,
  loading: true,
  refreshUser: async () => {},
});

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);

  const fetchUser = async (uid: string): Promise<boolean> => {
    try {
      const docRef = doc(db, 'users', uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setUser(docSnap.data() as User);
        return true;
      } else {
        setUser(null);
        return false;
      }
    } catch (error) {
      console.error('❌ Failed to fetch user from Firestore:', error);
      setUser(null);
      return false;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setLoading(true);
      if (firebaseUser) {
        const exists = await fetchUser(firebaseUser.uid);
        if (!exists) {
          setShowPopup(true);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const refreshUser = async () => {
    const current = auth.currentUser;
    if (current) {
      const exists = await fetchUser(current.uid);
      if (!exists) setShowPopup(true);
    }
  };

  return (
    <UserContext.Provider value={{ user, loading, refreshUser }}>
      {children}
      {showPopup && (
        <UserOnboardingPopup
          onClose={async () => {
            setShowPopup(false);
            await refreshUser();
          }}
        />
      )}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
